/**
 * Products Component - Compact horizontal product layout with 2-column grid
 */

import React from 'react';
import { Product, CartItem, ProductCategory, UIText, Currency } from './types';
import { getImageUrl, formatPrice, getEventCurrency, getVatDisplayEnabled, getVatRate, calculateGrossPrice } from './utils';

interface ProductsProps {
	products: Product[];
	cartItems: CartItem[];
	onAddToCart: ( item: CartItem ) => void;
	onUpdateQuantity: (
		productId: string,
		quantity: number,
		variant?: string
	) => void;
	uiText: UIText[ 'products' ];
}

export const Products: React.FC< ProductsProps > = ( {
	products,
	cartItems,
	onAddToCart,
	onUpdateQuantity,
	uiText,
} ) => {
	const categories: ProductCategory[] = [
		'Hardware',
		'Accessories',
		'Resins',
		'Cápsulas Midas',
	];

	const handleQuantityChange = (
		product: Product,
		newQuantity: number,
		variant?: string
	) => {
		const quantity = Math.max( 0, parseInt( newQuantity.toString() ) || 0 );

		// Always update quantity, even if it's 0
		const existingItem = cartItems.find(
			( item ) =>
				item.product.id === product.id &&
				item.selectedVariant === variant
		);

		if ( existingItem ) {
			onUpdateQuantity( product.id, quantity, variant );
		} else if ( quantity > 0 || ( variant && hasVariants( product ) ) ) {
			// Add item if quantity > 0, OR if it's a variant product (to create the row with quantity 0)
			onAddToCart( {
				product,
				quantity,
				selectedVariant: variant,
			} );
		}
	};

	const hasVariants = ( product: Product ): boolean => {
		return !! ( product.variants && product.variants.length > 0 );
	};

	const handleIncrement = ( product: Product, variant?: string ) => {
		const existingItem = cartItems.find(
			( item ) =>
				item.product.id === product.id &&
				item.selectedVariant === variant
		);
		const currentQuantity = existingItem?.quantity || 0;
		handleQuantityChange( product, currentQuantity + 1, variant );
	};

	const handleDecrement = ( product: Product, variant?: string ) => {
		const existingItem = cartItems.find(
			( item ) =>
				item.product.id === product.id &&
				item.selectedVariant === variant
		);
		const currentQuantity = existingItem?.quantity || 0;
		if ( currentQuantity > 0 ) {
			handleQuantityChange( product, currentQuantity - 1, variant );
		}
	};

	const getPlaceholderUrl = ( productName: string ) => {
		// Create a clean placeholder URL with product name
		const encodedName = encodeURIComponent( productName );
		return `https://placehold.co/80x80/e0e0e0/666666?text=${ encodedName }`;
	};

	const getProductImageUrl = ( product: Product ) => {
		// Use actual image path if available, otherwise use placeholder
		if ( product.imagePath ) {
			return getImageUrl( product.imagePath );
		}
		return getPlaceholderUrl( product.name );
	};

	// Helper function to render the "add new variant" row
	const renderAddVariantRow = (
		product: Product,
		productCartItems: CartItem[]
	) => {
		const availableVariants = product.variants!.filter(
			( v ) =>
				! productCartItems.some(
					( item ) => item.selectedVariant === v.value
				)
		);
		if ( availableVariants.length === 0 ) return null;

		return (
			<div className="sprintry-product-card__variant-row">
				<select
					className="sprintry-product-card__variant"
					onChange={ ( e ) => {
						const variant = e.target.value;
						// Add with quantity 0 when variant is selected
						handleQuantityChange( product, 0, variant );
					} }
					value=""
				>
					<option value="" disabled>
						{ uiText.selectVariant }
					</option>
					{ availableVariants.map( ( v ) => (
						<option key={ v.value } value={ v.value }>
							{ v.label }
						</option>
					) ) }
				</select>
				<div className="sprintry-product-card__quantity-controls">
					<button
						type="button"
						className="sprintry-product-card__stepper-btn"
						disabled
						aria-label="Decrease quantity"
					>
						−
					</button>
					<button
						type="button"
						className="sprintry-product-card__stepper-btn"
						disabled
						aria-label="Increase quantity"
					>
						+
					</button>
					<input
						type="number"
						className="sprintry-product-card__quantity-input"
						min="0"
						value=""
						placeholder="0"
						disabled
					/>
				</div>
			</div>
		);
	};

	return (
		<section className="sprintry-products">
			<h3 className="sprintry-products__title">{ uiText.title }</h3>

			{ /* Render each category as a separate section */ }
			{ categories.map( ( category ) => {
				const categoryProducts = products.filter(
					( p ) => p.category === category
				);

				return (
					<div
						key={ category }
						className="sprintry-products__section"
					>
						<h4 className="sprintry-products__category-title">
							{ uiText.categories[ category ] || category }
						</h4>

						{ /* Products List - 2 column grid with horizontal cards */ }
						<div className="sprintry-products__list">
							{ categoryProducts.map( ( product ) => {
								const productCartItems = cartItems.filter(
									( item ) => item.product.id === product.id
								);

								// For products without variants, get the single cart item quantity
								const mainQuantity = ! hasVariants( product )
									? productCartItems[ 0 ]?.quantity || 0
									: 0;

								return (
									<div
										key={ product.id }
										className="sprintry-product-card sprintry-product-card--horizontal"
									>
										<div
											className="sprintry-product-card__thumbnail"
											style={ {
												backgroundImage: `url(${ getProductImageUrl(
													product
												) })`,
											} }
										></div>

										<div className="sprintry-product-card__info">
											<div className="sprintry-product-card__header">
												<div className="sprintry-product-card__title-section">
													<h4 className="sprintry-product-card__name">
														{ product.name }
													</h4>
													{ product.notes && (
														<p className="sprintry-product-card__notes">
															{ product.notes }
														</p>
													) }
													{ /* Serial Number - small text under product name */ }
													<p className="sprintry-product-card__serial-number">
														{ product.serialNumber ||
															'Serial#' }
													</p>
												</div>
												<div className="sprintry-product-card__price-section">
													<div className="sprintry-product-card__price">
														{ getVatDisplayEnabled() ? (
															<span>
																{ formatPrice( calculateGrossPrice( product.price, getVatRate() ), product.currency || getEventCurrency() ) }
																<span className="sprintry-product-card__price-label">
																	{ uiText.vatIncluded || 'incl. VAT' }
																</span>
															</span>
														) : (
															formatPrice( product.price, product.currency || getEventCurrency() )
														) }
													</div>
													{ getVatDisplayEnabled() && (
														<div className="sprintry-product-card__vat-price">
															{ formatPrice( product.price, product.currency || getEventCurrency() ) } { uiText.vatExcluded || 'excl. VAT' }
														</div>
													) }
												</div>
											</div>

											{ /* For products with variants, append variant rows with quantity > 0 */ }
											{ hasVariants( product ) && (
												<>
													{ /* Render existing cart items */ }
													{ productCartItems.map(
														( cartItem ) => {
															if (
																! cartItem.selectedVariant
															)
																return null;

															return (
																<div
																	key={
																		cartItem.selectedVariant
																	}
																	className="sprintry-product-card__variant-row"
																>
																	<select
																		className="sprintry-product-card__variant"
																		value={
																			cartItem.selectedVariant
																		}
																		onChange={ (
																			e
																		) => {
																			const newVariant =
																				e
																					.target
																					.value;
																			// First, set quantity of current variant to 0
																			handleQuantityChange(
																				product,
																				0,
																				cartItem.selectedVariant
																			);
																			// Then, set the new variant with the same quantity
																			handleQuantityChange(
																				product,
																				cartItem.quantity,
																				newVariant
																			);
																		} }
																	>
																		{ product.variants!.map(
																			(
																				v
																			) => (
																				<option
																					key={
																						v.value
																					}
																					value={
																						v.value
																					}
																				>
																					{
																						v.label
																					}
																				</option>
																			)
																		) }
																	</select>
																	<div className="sprintry-product-card__quantity-controls">
																		<button
																			type="button"
																			className="sprintry-product-card__stepper-btn"
																			onClick={ () =>
																				handleDecrement(
																					product,
																					cartItem.selectedVariant
																				)
																			}
																			disabled={
																				cartItem.quantity ===
																				0
																			}
																			aria-label="Decrease quantity"
																		>
																			−
																		</button>
																		<button
																			type="button"
																			className="sprintry-product-card__stepper-btn"
																			onClick={ () =>
																				handleIncrement(
																					product,
																					cartItem.selectedVariant
																				)
																			}
																			aria-label="Increase quantity"
																		>
																			+
																		</button>
																		<input
																			type="number"
																			className="sprintry-product-card__quantity-input"
																			min="0"
																			value={
																				cartItem.quantity ===
																				0
																					? ''
																					: cartItem.quantity
																			}
																			placeholder="0"
																			onChange={ (
																				e
																			) => {
																				const value =
																					e
																						.target
																						.value;
																				// Handle empty input as 0
																				if (
																					value ===
																					''
																				) {
																					handleQuantityChange(
																						product,
																						0,
																						cartItem.selectedVariant
																					);
																				} else {
																					handleQuantityChange(
																						product,
																						parseInt(
																							value
																						) ||
																							0,
																						cartItem.selectedVariant
																					);
																				}
																			} }
																			onFocus={ (
																				e
																			) => {
																				// Select all text on focus to replace the value
																				e.target.select();
																			} }
																			onClick={ (
																				e
																			) => {
																				// Also select on click in case it's already focused
																				const input =
																					e.target as HTMLInputElement;
																				input.select();
																			} }
																		/>
																	</div>
																</div>
															);
														}
													) }

													{ /* Show default variant row if no cart items exist */ }
													{ productCartItems.length ===
														0 && (
														<div className="sprintry-product-card__variant-row">
															<select
																className="sprintry-product-card__variant"
																value={
																	product.variants![ 0 ]
																		.value
																}
																onChange={ (
																	e
																) => {
																	const newVariant =
																		e.target
																			.value;
																	// Switch to the new variant with quantity 0
																	handleQuantityChange(
																		product,
																		0,
																		newVariant
																	);
																} }
															>
																{ product.variants!.map(
																	( v ) => (
																		<option
																			key={
																				v.value
																			}
																			value={
																				v.value
																			}
																		>
																			{
																				v.label
																			}
																		</option>
																	)
																) }
															</select>
															<div className="sprintry-product-card__quantity-controls">
																<button
																	type="button"
																	className="sprintry-product-card__stepper-btn"
																	onClick={ () =>
																		handleDecrement(
																			product,
																			product.variants![ 0 ]
																				.value
																		)
																	}
																	disabled
																	aria-label="Decrease quantity"
																>
																	−
																</button>
																<button
																	type="button"
																	className="sprintry-product-card__stepper-btn"
																	onClick={ () =>
																		handleIncrement(
																			product,
																			product.variants![ 0 ]
																				.value
																		)
																	}
																	aria-label="Increase quantity"
																>
																	+
																</button>
																<input
																	type="number"
																	className="sprintry-product-card__quantity-input"
																	min="0"
																	value=""
																	placeholder="0"
																	onChange={ (
																		e
																	) => {
																		const value =
																			e
																				.target
																				.value;
																		// Handle empty input as 0
																		if (
																			value ===
																			''
																		) {
																			handleQuantityChange(
																				product,
																				0,
																				product.variants![ 0 ]
																					.value
																			);
																		} else {
																			handleQuantityChange(
																				product,
																				parseInt(
																					value
																				) ||
																					0,
																				product.variants![ 0 ]
																					.value
																			);
																		}
																	} }
																	onFocus={ (
																		e
																	) => {
																		// Select all text on focus to replace the value
																		e.target.select();
																	} }
																	onClick={ (
																		e
																	) => {
																		// Also select on click in case it's already focused
																		const input =
																			e.target as HTMLInputElement;
																		input.select();
																	} }
																/>
															</div>
														</div>
													) }

													{ /* Show "add new variant" row only when all cart items have quantity > 0 */ }
													{ productCartItems.length >
														0 &&
														productCartItems.every(
															( item ) =>
																item.quantity >
																0
														) &&
														renderAddVariantRow(
															product,
															productCartItems
														) }
												</>
											) }

											{ /* For products without variants, show quantity controls in a row */ }
											{ ! hasVariants( product ) && (
												<div className="sprintry-product-card__quantity-row">
													<div className="sprintry-product-card__quantity-controls">
														<button
															type="button"
															className="sprintry-product-card__stepper-btn"
															onClick={ () =>
																handleDecrement(
																	product
																)
															}
															disabled={
																mainQuantity ===
																0
															}
															aria-label="Decrease quantity"
														>
															−
														</button>
														<button
															type="button"
															className="sprintry-product-card__stepper-btn"
															onClick={ () =>
																handleIncrement(
																	product
																)
															}
															aria-label="Increase quantity"
														>
															+
														</button>
														<input
															type="number"
															className="sprintry-product-card__quantity-input"
															min="0"
															value={
																mainQuantity ===
																0
																	? ''
																	: mainQuantity
															}
															placeholder="0"
															onChange={ (
																e
															) => {
																const value =
																	e.target
																		.value;
																// Handle empty input as 0
																if (
																	value === ''
																) {
																	handleQuantityChange(
																		product,
																		0
																	);
																} else {
																	handleQuantityChange(
																		product,
																		parseInt(
																			value
																		) || 0
																	);
																}
															} }
															onFocus={ ( e ) => {
																// Select all text on focus to replace the value
																e.target.select();
															} }
															onClick={ ( e ) => {
																// Also select on click in case it's already focused
																const input =
																	e.target as HTMLInputElement;
																input.select();
															} }
														/>
													</div>
												</div>
											) }
										</div>
									</div>
								);
							} ) }
						</div>
					</div>
				);
			} ) }
		</section>
	);
};
